import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { message, theme, Result, Button } from 'antd';

import ListingsFilter from './components/ListingsFilter';
import ListingsList from './components/ListingsList';
import './Listings.css';
import { FILTER_TYPES, isValidFilterType } from './constants/filterTypes';
import {
  fetchListings,
  handleSearchChange,
  handleFulfillmentChannelChange,
  handleStatusChange,
  handleSellerAccountChange,
  handleClearFilters,
  applyFilters,
  handlePaginationChange,
  handleExpandedRowsChange
} from './actions';
import { setFilters, setPagination } from './reducers';

const Listings = ({
  // Props từ mapStateToProps
  listings,
  loading,
  pagination,
  filters,
  expandedRowKeys,
  user, // Thêm user từ auth state
  // Props từ mapDispatchToProps (action creators đã được bind)
  fetchListings,
  handleSearchChange,
  handleFulfillmentChannelChange,
  handleStatusChange,
  handleSellerAccountChange,
  handleClearFilters,
  applyFilters,
  handlePaginationChange,
  handleExpandedRowsChange,
  setFilters,
  setPagination
}) => {
  const navigate = useNavigate();
  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();

  const [isInitialized, setIsInitialized] = useState(false);

  // Initialize page on mount
  useEffect(() => {
    if (!isInitialized) {
      setIsInitialized(true);
      fetchListings(pagination.current, pagination.pageSize);
    }
  }, [isInitialized, fetchListings, pagination.current, pagination.pageSize]);

  // Generic filter handlers with validation
  const filterHandlers = useMemo(() => ({
    [FILTER_TYPES.SEARCH]: handleSearchChange,
    [FILTER_TYPES.FULFILLMENT_CHANNEL]: handleFulfillmentChannelChange,
    [FILTER_TYPES.STATUS]: handleStatusChange,
    [FILTER_TYPES.SELLER_ACCOUNT]: handleSellerAccountChange
  }), [
    handleSearchChange,
    handleFulfillmentChannelChange,
    handleStatusChange,
    handleSellerAccountChange
  ]);

  // Generic filter change handler with validation
  const handleFilterChange = useCallback((filterType, value) => {
    if (!isValidFilterType(filterType)) {
      console.warn(`Invalid filter type: ${filterType}. Available types:`, Object.values(FILTER_TYPES));
      return;
    }

    const handler = filterHandlers[filterType];
    if (handler) {
      handler(value);
    } else {
      console.warn(`No handler found for filter type: ${filterType}`);
    }
  }, [filterHandlers]);

  const handleClearAllFilters = useCallback(() => {
    handleClearFilters();
  }, [handleClearFilters]);

  const handleApplyFilters = useCallback(() => {
    applyFilters();
  }, [applyFilters]);

  const handleReload = useCallback((options = {}) => {
    fetchListings(pagination.current, pagination.pageSize);
  }, [fetchListings, pagination.current, pagination.pageSize]);

  // Memoize style objects to prevent re-creation
  const mainContainerStyle = useMemo(() => ({
    margin: '0px 10px 10px 10px',
    padding: '5px 16px 16px 16px',
    background: colorBgContainer,
    borderRadius: borderRadiusLG,
    minHeight: 280,
  }), [colorBgContainer, borderRadiusLG]);

  // Check permissions - tạm thời sử dụng inventory permissions
  // const canViewListings = user?.role?.permissions?.some(p => 
  //   p.code === 'inventory.read' || p.code === 'sku_item.read'
  // ) || false;

  // // If user doesn't have permission to view listings, show access denied
  // if (!canViewListings) {
  //   return (
  //     <Result
  //       status="403"
  //       title="403"
  //       subTitle="Bạn không có quyền truy cập vào trang này."
  //       extra={
  //         <Button type="primary" onClick={() => navigate('/')}>
  //           Quay về trang chủ
  //         </Button>
  //       }
  //     />
  //   );
  // }

  return (
    <div>
      <div
        style={{
          margin: '10px 10px 0px 10px',
        }}
      >
        <ListingsFilter
          onFilterChange={handleFilterChange}
          onClearFilters={handleClearAllFilters}
          onApplyFilters={handleApplyFilters}
          onReload={handleReload}
          filters={filters}
          loading={loading}
        />
      </div>
      <div style={mainContainerStyle}>
        <ListingsList
          listings={listings}
          isLoading={loading}
          pagination={pagination}
          expandedRowKeys={expandedRowKeys}
          onExpandedRowsChange={handleExpandedRowsChange}
        />
      </div>
    </div>
  );
};

// mapStateToProps - Chỉ lấy những state cần thiết
const mapStateToProps = (state) => {
  const listingsState = state.listings;
  const authState = state.auth;
  return {
    listings: listingsState.listings,
    loading: listingsState.loading,
    pagination: listingsState.pagination,
    filters: listingsState.filters,
    expandedRowKeys: listingsState.expandedRowKeys,
    user: authState.user, // Thêm user từ auth state
  };
};

// mapDispatchToProps - Sử dụng object notation để tự động bind actions
const mapDispatchToProps = {
  fetchListings,
  handleSearchChange,
  handleFulfillmentChannelChange,
  handleStatusChange,
  handleSellerAccountChange,
  handleClearFilters,
  applyFilters,
  handlePaginationChange,
  handleExpandedRowsChange,
  setFilters,
  setPagination
};

export default connect(mapStateToProps, mapDispatchToProps)(Listings);